<template>
    <div class="flex flex-col md:flex-row items-start md:items-center gap-1.75 md:gap-9 w-full">
        <form @submit.prevent="search" class="w-full">
            <div class="bg-gold-lighter flex h-11 w-full sm:w-88 items-center">
                <input v-model="currentQuery" type="text" name="search" class="placeholder:text-primary placeholder:h7 grow border-none pl-4 text-xs focus:outline-none" placeholder="City & State or ZIP Code" />

                <button v-if="searchedQuery && !searching" type="button" class="hover:bg-gold-light flex size-8 shrink-0 cursor-pointer items-center justify-center transition-colors hover:text-white" @click.prevent="reset">
                    <XIcon class="size-5" />
                </button>

                <button v-if="!searching" type="submit" class="hover:bg-gold-light mr-1.5 flex size-8 shrink-0 cursor-pointer items-center justify-center transition-colors hover:text-white" aria-label="submit restaurants search">
                    <SearchIcon class="size-5" />
                </button>

                <Spinner v-if="searching" class="mr-3 size-5" />
            </div>
        </form>

        <button v-if="canGeolocate" type="button" :disabled="geolocationLoading" class="grow shrink-0 h7 text-turquoise hover:text-gold inline-flex cursor-pointer items-center gap-x-1.75 leading-0 transition-colors" @click.prevent="geolocate">
            <Spinner v-if="geolocationLoading" theme="turquoise" class="size-4.5" />
            <CrosshairIcon v-else class="size-4.5" />
            Use my location
        </button>
    </div>
</template>

<script setup>
import { CrosshairIcon, SearchIcon, XIcon } from '@lucide/vue';
import axios from 'axios';
import { inject } from 'vue';
import Spinner from '../Ui/Spinner.vue';
import { useRestaurantsListStore } from '../../stores/restaurant-list-store';
import { useRestaurantsListSearchStore } from '../../stores/restaurant-list-search-store.js';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';

const dashboardApiBase = inject('dashboardApiBase');

const router = useRouter();

const store = useRestaurantsListStore();
const searchStore = useRestaurantsListSearchStore();

const { setSearchedCoords } = store;

const { reset } = searchStore;
const { currentQuery, searchedQuery, searching, geolocationLoading } = storeToRefs(searchStore);

/**
 * Perform a search request.
 */
function search() {
    searching.value = true;
    searchedQuery.value = currentQuery.value;

    axios
        .get(`${dashboardApiBase}/utils/geocode`, { params: { address: searchedQuery.value } })
        .then(({ data }) => {
            clearPage();
            setSearchedCoords(data.data.location);
        })
        .catch((err) => console.error(err))
        .finally(() => (searching.value = false));
}

/**
 * Clear the page route param.
 *
 * This is in place to return to page 1 when performing a search.
 */
function clearPage() {
    const target = router.currentRoute.value.path.replace(/\d+\/?/, '');

    router.push({ path: target });
}

/**
 * Determine whether the browser can perform geolocation.
 *
 * @returns {boolean} Whether the browser can perform geolocation.
 */
function canGeolocate() {
    return 'geolocation' in navigator;
}

/**
 * Perform a geolocation-based search.
 */
function geolocate() {
    geolocationLoading.value = true;

    navigator.geolocation.getCurrentPosition(handleGeolocationResult, handleGeolocationError);
}

/**
 * Handle a successful geolocation result.
 *
 * @param {GeolocationPosition} data The geolocation result data.
 */
function handleGeolocationResult(data) {
    clearPage();
    setSearchedCoords({ lat: data.coords.latitude, lng: data.coords.longitude });
    currentQuery.value = 'My Location';
    searchedQuery.value = currentQuery.value;
    geolocationLoading.value = false;
}

/**
 * Handle a geolocation error.
 *
 * @param {GeolocationPositionError} err The geolocation error data.
 */
function handleGeolocationError(err) {
    alert(err.message);
    geolocationLoading.value = false;
}
</script>
